import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

export interface Tenant {
  tenantId: string;
  name: string;
  subdomain: string;
}

interface TenantContextType {
  selectedTenant: Tenant | null;
  tenants: Tenant[];
  setSelectedTenant: (tenantId: string) => void;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const API_BASE_URL = '/api/v1';
const SESSION_STORAGE_KEY = 'selectedTenantId';

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenantState] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set up axios interceptor to read tenant ID from sessionStorage on each request
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const tenantId = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (tenantId) {
        config.headers['X-Selected-Tenant-ID'] = tenantId;
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  // Load tenants and initial selection on mount
  useEffect(() => {
    // For non-global admins, set selected tenant to their home tenant
    if (!user?.isGlobalAdmin) {
      if (user?.tenantId) {
        const homeTenant: Tenant = {
          tenantId: user.tenantId,
          name: user.tenantName || user.tenantId,
          subdomain: user.tenantId,
        };
        setSelectedTenantState(homeTenant);
        sessionStorage.setItem(SESSION_STORAGE_KEY, user.tenantId);
      }
      setLoading(false);
      return;
    }

    const loadTenants = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all available tenants - cookie is sent automatically via withCredentials
        const response = await axios.get(`${API_BASE_URL}/tenants`);
        // Handle BigIntSerializationInterceptor: response.data.data
        const responseData = response.data?.data || response.data || [];
        const tenantList: Tenant[] = Array.isArray(responseData) ? responseData : [];
        setTenants(tenantList);

        // Determine initial tenant:
        // 1. Check sessionStorage for previously selected tenant (per-tab isolation)
        // 2. Fall back to admin's home tenant (user.tenantId)
        const storedTenantId = sessionStorage.getItem(SESSION_STORAGE_KEY);
        const initialTenant = tenantList.find(
          t => t.tenantId === storedTenantId
        ) || tenantList.find(
          t => t.tenantId === user.tenantId
        ) || tenantList[0]; // Fallback to first tenant

        if (initialTenant) {
          setSelectedTenantState(initialTenant);
          sessionStorage.setItem(SESSION_STORAGE_KEY, initialTenant.tenantId);
        }
      } catch (err) {
        console.error('Failed to load tenants:', err);
        setError('Failed to load tenants');
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
  }, [user]);

  const setSelectedTenant = useCallback((tenantId: string) => {
    const tenant = tenants.find(t => t.tenantId === tenantId);
    if (tenant) {
      setSelectedTenantState(tenant);
      sessionStorage.setItem(SESSION_STORAGE_KEY, tenantId);
      // Navigate to dashboard to refresh data (no full page reload)
      navigate('/admin/dashboard');
    }
  }, [tenants, navigate]);

  return (
    <TenantContext.Provider
      value={{
        tenants,
        selectedTenant,
        setSelectedTenant,
        loading,
        error,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
